import { Component, AfterViewInit, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AdminService } from '../../app/admin/admin.service';
import { AppComponent } from '../../app/app.component';
import { ILatestOnlineOrder } from '../../app/onlineorder/latestonlineorder';
import { INuveiPaymentStatusResponse } from '../../app/onlineorder/nuveiGetPaymentStatusResponse';
import { INuveiOpenOrderResponse } from '../../app/onlineorder/nuveiopenorderresponse';
import { INuveiTransactionResponse } from '../../app/onlineorder/nuveiPaymentResponse';
import { IDmnResult } from '../../app/onlineorder/nuveiDMNResponse';
import { OnlineOrderService } from '../../app/onlineorder/onlineorder.service';
import { IOrderValidations } from '../../app/onlineorder/ordervalidations';
import { IShipMethod } from '../../app/onlineorder/shipmethod';
import { LoginService } from '../../app/home/login.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { INuveiInitSessionResponse } from '../onlineorder/NuveiInitSessionResponse';
import { ICustomerInfoResponse } from '../account/customerinforesponse';
import { OrderService } from '../onlineorder/order.service';

declare var checkout: any;

@Component({
  standalone: false,
  selector: 'NuveiPaymentsComponent',
  templateUrl: 'nuveipayments.component.html',
})
export class NuveiPaymentsComponent implements AfterViewInit, OnDestroy {

  // ── Watchdog ─────────────────────────────────────────────────────────────────
  private checkoutStartedAt!: number;
  private checkoutIntervalHandle: any = null;
  private readonly CHECKOUT_MAX_AGE_MS = 10 * 60 * 1000;
  private readonly CHECK_INTERVAL_MS   = 15 * 1000;

  // ── Lifecycle / guard flags ───────────────────────────────────────────────────
  private destroy$              = new Subject<void>();
  private destroyed             = false;
  private checkoutInitialized   = false;
  private finalizationInProgress = false;

  // ── Order / payment state ─────────────────────────────────────────────────────
  public  orderNumber:       any;
  public  total:             number;
  public  dmnRes:            IDmnResult;
  public  currentUser:       any;
  public  nuveiTransRes:     INuveiTransactionResponse;
  public  nvPayStatusRes:    INuveiPaymentStatusResponse;
  public  nvInitResponse:    INuveiInitSessionResponse;
  public  allCustomerInfo:   ICustomerInfoResponse;
  public  latestOnlineOrder: ILatestOnlineOrder;
  public  shipMethod:        IShipMethod;

  nuveiSessionToken: string;
  nuveiOpenOrderRes: INuveiOpenOrderResponse;
  cardHolderName:    string;
  isDataLoaded       = false;

  // ── Kept to avoid breaking any template bindings ──────────────────────────────
  orderValidations:  IOrderValidations;
  translatedText     = '';
  showPaymentForm    = false;
  paymentCreated     = false;
  saveSuccess:       any;
  addMailingSuccess: any;
  caption:           string;
  transErrMsg:       string;

  constructor(
    private orderSvc:      OnlineOrderService,
    private router:        Router,
    private zone:          NgZone,
    public  translate:     TranslateService,
    public  admin:         AdminService,
    public  spinner:       NgxSpinnerService,
    private toaster:       ToastrService,
    public  app:           AppComponent,
    private _loginService: LoginService,
    private orderService:  OrderService,
  ) {}

  // ── Lifecycle hooks ───────────────────────────────────────────────────────────

  ngAfterViewInit(): void {
    if (!this.checkoutInitialized) {
      this.initCheckout();
    }
  }

  ngOnDestroy(): void {
    this.stopCheckoutWatchdog();
    this.teardownNuvei();
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Watchdog ──────────────────────────────────────────────────────────────────

  private startCheckoutWatchdog(): void {
    this.stopCheckoutWatchdog();
    this.checkoutStartedAt = Date.now();

    this.checkoutIntervalHandle = setInterval(() => {
      if (Date.now() - this.checkoutStartedAt >= this.CHECKOUT_MAX_AGE_MS) {
        console.warn('⏱️ Nuvei checkout exceeded 10 minutes. Forcing cancel.');
        this.zone.run(() => {
          this.stopCheckoutWatchdog();
          this.forceExitCheckoutDueToTimeout();
        });
      }
    }, this.CHECK_INTERVAL_MS);
  }

  private stopCheckoutWatchdog(): void {
    if (this.checkoutIntervalHandle) {
      clearInterval(this.checkoutIntervalHandle);
      this.checkoutIntervalHandle = null;
    }
  }

  private forceExitCheckoutDueToTimeout(): void {
    this.deferredDomCleanup();
    localStorage.removeItem('nuveiSessionToken');
    localStorage.removeItem('merchantInfoTemp');
    this.spinner.hide('processingOrder');
    this.showError(
      'Checkout.nuvei.payments.sessionExpired',
      'Checkout.nuvei.payments.sessionExpiredTitle',
      true,
    );
    this.checkoutInitialized = false;
    this.cancelOrder();
  }

  // ── Session token handling ────────────────────────────────────────────────────

  private handleInvalidSessionToken(): void {
    this.stopCheckoutWatchdog();
    console.warn('🔁 Nuvei session token expired. Restarting checkout.');
    localStorage.removeItem('nuveiSessionToken');
    localStorage.removeItem('merchantInfoTemp');
    this.spinner.hide('processingOrder');
    this.showError(
      'Checkout.nuvei.payments.sessionExpired',
      'Checkout.nuvei.payments.sessionExpiredTitle',
      true,
    );
    this.checkoutInitialized = false;
    this.initCheckout();
  }

  // ── Checkout init ─────────────────────────────────────────────────────────────

  /**
   * Main initializer for the Nuvei SimplyConnect checkout.
   * Handles session token, open order creation, form rendering, and the full
   * post-payment flow: PaymentStatus → DMN polling → BDTCCStoreNuvei → navigate.
   */
  initCheckout(): void {
    if (this.checkoutInitialized) {
      console.warn('initCheckout already running — skipping duplicate call.');
      return;
    }
    this.checkoutInitialized   = true;
    this.finalizationInProgress = false;

    this.currentUser       = JSON.parse(localStorage.getItem('authorizationData'));
    this.shipMethod        = JSON.parse(localStorage.getItem('shipMethod'));
    this.latestOnlineOrder = JSON.parse(localStorage.getItem('orderId'));

    const userTokenId = this.buildUserTokenId();
    const locale      = this.getLocale();
    const myTotal     = this.amountToPay().toString();

    this.orderSvc.getSessionToken()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: INuveiInitSessionResponse) => {
          this.nvInitResponse    = data;
          this.nuveiSessionToken = data.SessionToken;
          this.allCustomerInfo   = JSON.parse(localStorage.getItem('allCustInfo'));
          localStorage.setItem('nuveiSessionToken', data.SessionToken);
          localStorage.setItem('merchantInfoTemp', JSON.stringify(data));
          console.log('Nuvei session token:', data.SessionToken);
        },
        error: () => {
          this.checkoutInitialized = false;
          this.spinner.hide('processingOrder');
          this.showError(
            'Checkout.nuvei.simplyConnect.loadFailure',
            'Checkout.nuvei.simplyConnect.loadFailureTitle',
            true,
          );
        },
        complete: () => {
          // ── Open order ──────────────────────────────────────────────────────
          this.orderSvc
            .createOpenOrder(this.nuveiSessionToken, this.shipMethod.BalanceDue.toString())
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next:  (res) => (this.nuveiOpenOrderRes = res),
              error: (err) => {
                console.error('createOpenOrder failed:', err);
                this.checkoutInitialized = false;
              },
              complete: async () => {
                if (this.destroyed) {
                  this.checkoutInitialized = false;
                  return;
                }

                await this.ensureNuveiCheckoutJs();

                const host = document.querySelector('#nuvei-payment-form');
                if (!host) {
                  console.error('Nuvei payment host element not found.');
                  this.checkoutInitialized = false;
                  return;
                }

                // ── Render ────────────────────────────────────────────────────
                // NOTE: onReady / onResult must remain as inline closures here
                // so they close over myTotal, userTokenId, locale, and the
                // finalizationInProgress flag correctly. Extracting them to
                // class methods breaks the Nuvei SDK rendering entirely.
                const email = this.buildEmail();

                checkout({
                  env:             'prod',
                  showCardLogos:   true,
                  renderTo:        '#nuvei-payment-form',
                  sessionToken:    this.nuveiSessionToken,
                  merchantSiteId:  this.nvInitResponse.MerchantSiteId,
                  merchantId:      this.nvInitResponse.MerchantId,
                  currency:        'USD',
                  amount:          myTotal,
                  country:         'US',
                  locale,
                  userId:          userTokenId,
                  theme:           'tiles',
                  selectPM:        'cc_card',
                  billingAddress: {
                    firstName: this.allCustomerInfo.FirstName,
                    lastName:  this.allCustomerInfo.LastName,
                    email,
                    country:   'US',
                  },
                  pmBlacklist: [
                    'apmgw_expresscheckout',
                    'applePay',
                    'googlePay',
                    'apmgw_googlepay',
                    'apmgw_applepay',
                    'ppp_GooglePay',
                    'ppp_ApplePay',
                  ],
                  apmConfig:     { cc_card: { savePM: false } },
                  paymentOption: { savePm: false },
                  fullName:      `${this.allCustomerInfo.FirstName} ${this.allCustomerInfo.LastName}`,
                  email,
                  showResponseMessage: false,
                  maskCvv:          true,
                  autoOpenPM:       true,
                  alwaysCollectCvv: true,
                  logLevel:         1,
                  savePm:           false,

                  onReady: (_result: any) => {
                    console.log('Nuvei checkout ready.');
                    this.startCheckoutWatchdog();
                  },

                  onFormValidated: (result: any) => {
                    console.log('Form validated:', result);
                  },

                  onDeclineRecovery: (_result: any) => {},

                  onResult: (result: any) => {
                    console.log('Nuvei onResult fired:', result);

                    // Session expired
                    if (result?.session_expired === true) {
                      this.stopCheckoutWatchdog();
                      this.spinner.hide('processingOrder');
                      this.handleInvalidSessionToken();
                      this.showError(
                        'Checkout.nuvei.payments.sessionExpired',
                        'Checkout.nuvei.payments.sessionExpiredTitle',
                        true,
                      );
                      this.cancelOrder();
                      return;
                    }

                    // Non-approved (declined, pending, etc.)
                    if (result?.transactionStatus !== 'APPROVED') {
                      console.warn('Non-approved onResult:', result?.transactionStatus);
                      this.showError(
                        (result?.errorDescription || 'Payment declined') + '. Please try again.',
                        'Payment Failed',
                        false,
                      );
                      return;
                    }

                    // Duplicate approved callback guard
                    if (this.finalizationInProgress) {
                      console.warn('Finalization already running — ignoring duplicate onResult.');
                      return;
                    }
                    this.finalizationInProgress = true;
                    this.stopCheckoutWatchdog();

                    const txnId    = result?.transactionId;
                    const cardType = this.getCardType(result?.bin);

                    if (!txnId) {
                      this.resetFinalization();
                      this.showError('Missing transaction ID from Nuvei.', 'Error', false);
                      return;
                    }

                    this.zone.run(() => this.spinner.show('processingOrder'));
                    this.deferredDomCleanup();

                    // ── PaymentStatus ─────────────────────────────────────────
                    this.orderSvc
                      .getPaymentStatus(this.nuveiSessionToken, this.nuveiOpenOrderRes)
                      .pipe(takeUntil(this.destroy$))
                      .subscribe({
                        next:  (res1) => {
                          this.nvPayStatusRes = res1;
                          console.log('PaymentStatus2 response:', res1);
                        },
                        error: (err) => this.handlePaymentStatusError(err),
                        complete: () => {
                          const payStatus = String(
                            this.nvPayStatusRes?.TransactionStatus ||
                            this.nvPayStatusRes?.Status            ||
                            ''
                          ).toUpperCase();

                          if (payStatus && payStatus !== 'APPROVED') {
                            this.resetFinalization();
                            this.showError(
                              (this.nvPayStatusRes?.Reason ||
                               this.nvPayStatusRes?.GwErrorReason ||
                               'Payment could not be verified') + '. Please try again.',
                              'Payment Failed',
                              false,
                            );
                            return;
                          }

                          this.nuveiTransRes = { ...result, CardType: cardType };
                          console.log('Starting DMN poll for txnId:', txnId);

                          // ── DMN poll ────────────────────────────────────────
                          this.orderSvc
                            .pollDmnStatus2(txnId)
                            .pipe(takeUntil(this.destroy$))
                            .subscribe({
                              next: (res: IDmnResult) => {
                                console.log('DMN response:', res);
                                if (res?.Status === 'APPROVED') {
                                  this.dmnRes         = { ...res, CardType: cardType };
                                  this.cardHolderName = res.CardHolderName;
                                }
                              },
                              complete: () => {
                                console.log('🛑 DMN polling completed.');

                                if (!this.dmnRes || this.dmnRes.Status !== 'APPROVED') {
                                  this.resetFinalization();
                                  this.showError(
                                    'Payment was approved but confirmation was not received in time. Please contact support.',
                                    'DMN Timeout',
                                    false,
                                  );
                                  return;
                                }

                                // ── Store order ───────────────────────────────
                                this.orderSvc
                                  .BDTCCStoreNuvei(this.nuveiTransRes, this.dmnRes)
                                  .pipe(takeUntil(this.destroy$))
                                  .subscribe({
                                    next: (res1) => {
                                      console.log('BDTCCStoreNuvei:', res1);
                                      this.orderNumber = res1;
                                    },
                                    error: (err) => {
                                      this.resetFinalization();
                                      this.showError(
                                        err,
                                        'Checkout.component.error.title.error',
                                        false,
                                      );
                                    },
                                    complete: () => {
                                      localStorage.setItem(
                                        'completedOrder',
                                        JSON.stringify(this.orderNumber),
                                      );
                                      this.zone.run(() => this.spinner.hide('processingOrder'));
                                      this.router.navigateByUrl('completed/from/' + this.repId());
                                    },
                                  });
                              },
                              error: (err) => this.onDmnError(err),
                            });
                        },
                      });
                  },
                });
              },
            });
        },
      });

    this.isDataLoaded = true;
  }

  // ── Private helpers ───────────────────────────────────────────────────────────

  /** Resets finalization state and hides the spinner. */
  private resetFinalization(): void {
    this.finalizationInProgress = false;
    this.spinner.hide('processingOrder');
  }

  /** Builds the Nuvei userTokenId from the current user. */
  private buildUserTokenId(): string {
    return this.currentUser.userType === 'customer'
      ? 'C:' + this.currentUser.userName
      : 'R:' + this.currentUser.userName;
  }

  /**
   * Returns the customer email, falling back to FirstName+LastName@lbri.com
   * if none is on file. Centralised so it isn't duplicated in billingAddress
   * and the top-level email field.
   */
  private buildEmail(): string {
    return this.allCustomerInfo.Email
      ? this.allCustomerInfo.Email
      : `${this.allCustomerInfo.FirstName}${this.allCustomerInfo.LastName}@lbri.com`;
  }

  /**
   * Defers Nuvei overlay/popup DOM removal by 800ms so Nuvei's internal React
   * reconciliation finishes before we touch the DOM, avoiding removeChild errors.
   */
  private deferredDomCleanup(): void {
    setTimeout(() => {
      document
        .querySelectorAll('.sc-overlay, .sc-popup-container, #checkoutOverlay')
        .forEach((el) => {
          if (el?.parentNode) el.parentNode.removeChild(el);
        });
    }, 800);
  }

  private handlePaymentStatusError(err: any): void {
    this.resetFinalization();
    if (err?.status === 401) {
      this.handleInvalidSessionToken();
      return;
    }
    this.showError(err, 'Error', false);
  }

  private onDmnError(err: any): void {
    this.resetFinalization();
    if (err?.status === 401) {
      this.handleInvalidSessionToken();
      return;
    }
    this.showError(
      err?.error?.message || err?.message || 'Payment was approved, but DMN lookup failed.',
      'DMN Error',
      false,
    );
  }

  // ── Public helpers ────────────────────────────────────────────────────────────

  /** Returns the balance due from localStorage and caches it on the instance. */
  amountToPay(): number {
    const orderInfo = JSON.parse(localStorage.getItem('orderInfo'));
    this.shipMethod = orderInfo;
    this.total      = orderInfo.BalanceDue;
    console.log('Amount to pay: $' + this.total);
    return this.total;
  }

  sendToBeginning(): void {
    this.zone.run(() => {
      setTimeout(() => {
        this.router.navigate(['shop/from/' + localStorage.getItem('repId')]);
      }, 100);
    });
  }

  clearCart(): void {
    this.orderService.cart     = [];
    this.orderService.tempCart = [];
    this.app.myProdCount       = 0;
    this.total                 = 0;
    localStorage.removeItem('cartItems');
    localStorage.removeItem('prodCount');
    if (this._loginService.loggedIn()) {
      this.orderService.clearCart(false);
    }
  }

  cancelOrder(): void {
    this.stopCheckoutWatchdog();
    this.orderSvc.clearOrder()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (err) => console.error('clearOrder error:', err),
        complete: () => {
          ['validatedAddress', 'originalAddress', 'personalMessage', 'emailAddress',
           'orderId', 'nexioToken', 'nuveiSessionToken'].forEach((k) =>
            localStorage.removeItem(k)
          );
          this.orderSvc.latestOnlineOrder()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next:     (res) => (this.latestOnlineOrder = res),
              complete: () => {
                this.clearCart();
                localStorage.setItem('orderId', JSON.stringify(this.latestOnlineOrder));
                localStorage.removeItem('shipMethod');
                this.sendToBeginning();
              },
            });
        },
      });
  }

  showError(errorMessage: string, subject: string, staticErrorMsg: boolean): void {
    if (!staticErrorMsg) {
      const locale = this.getLocale();
      if (locale !== 'en') {
        this.admin.googleTranslate({ q: [errorMessage], target: locale }).subscribe({
          next: (response: any) => {
            this.transErrMsg = response.data.translations[0].translatedText as string;
          },
          complete: () => {
            this.translate.get(subject).subscribe((cap: string) => {
              this.toaster.error(this.transErrMsg, cap, { timeOut: 9000 });
            });
          },
        });
      } else {
        this.translate.get(subject).subscribe((cap: string) => {
          this.toaster.error(errorMessage, cap, { timeOut: 9000 });
        });
      }
    } else {
      this.translate.get(errorMessage).subscribe((msg: string) => (this.transErrMsg = msg));
      this.translate.get(subject).subscribe((cap: string) => (this.caption = cap));
      this.toaster.error(this.transErrMsg, this.caption, { timeOut: 9000 });
    }
  }

  repId(): string {
    return localStorage.getItem('repId');
  }

  getLocale(): string {
    return localStorage.getItem('locale');
  }

  getCardType(cardNumber: string): string | null {
    if (!cardNumber) return null;
    if (/^4/.test(cardNumber))                        return 'Visa';
    if (/^5[1-5]/.test(cardNumber))                   return 'MasterCard';
    if (/^2(2[2-9]|[3-6]\d|7[01])/.test(cardNumber)) return 'MasterCard';
    if (/^3[47]/.test(cardNumber))                    return 'American Express';
    if (/^3(0[0-5]|[68])/.test(cardNumber))           return 'Diners Club';
    if (/^6(011|5|4[4-9])/.test(cardNumber))          return 'Discover';
    if (/^35(2[89]|[3-8]\d)/.test(cardNumber))        return 'JCB';
    return null;
  }

  // ── Nuvei SDK helpers ─────────────────────────────────────────────────────────

  private ensureNuveiCheckoutJs(): Promise<void> {
    const id = 'nuvei-checkout-js';
    if (document.getElementById(id)) return Promise.resolve();

    return new Promise((resolve, reject) => {
      const s   = document.createElement('script');
      s.id      = id;
      s.src     = 'https://cdn.safecharge.com/safecharge_resources/v1/checkout/checkout.js';
      s.async   = false;
      s.defer   = true;
      s.onload  = () => resolve();
      s.onerror = () => reject(new Error('Failed to load Nuvei checkout.js'));
      document.body.appendChild(s);
    });
  }

  private teardownNuvei(): void {
    try {
      const c: any = (window as any).checkout;
      if (typeof c?.destroy === 'function') c.destroy();
    } catch { /* ignore */ }

    setTimeout(() => {
      document
        .querySelectorAll('.sc-overlay, .sc-popup-container, #checkoutOverlay')
        .forEach((el) => { if (el?.parentNode) el.parentNode.removeChild(el); });

      const host = document.getElementById('nuvei-payment-form');
      if (host) host.innerHTML = '';
    }, 800);
  }
}